/**
 * Catering date formatting helpers.
 *
 * All user-facing dates in the catering module render as numeric day/month/year
 * (e.g. 07/15/2026 for a US outlet, 15/07/2026 for an India outlet). Do NOT use
 * bare toLocaleDateString() / weekday / month-name formats for date values —
 * route them through here so the format stays consistent regardless of the
 * viewer's browser locale, and follows the selected outlet's country.
 */
import { getCurrencySymbolForCountry } from '../../../utils/currency';

// Bare "YYYY-MM-DD" (no time, no timezone) — a calendar date, not an instant.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a date value for display/comparison. A date-only string (no time,
 * no timezone — e.g. an invoice milestone's `dueDate`) is anchored to local
 * midnight so it renders as the literal calendar day it names. Left to plain
 * `new Date(...)`, JS parses date-only strings as UTC midnight per spec,
 * which then rolls back to the previous day once rendered in any timezone
 * behind UTC. Any value that already carries a time and/or timezone
 * (e.g. `paidAt`, `createdAt`) is a real instant and is parsed as-is so it
 * still converts to the viewer's local time, which is correct for those.
 */
export function parseDate(value?: string | number | Date | null): Date | null {
  if (value === undefined || value === null || value === '') return null;
  let d: Date;
  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) {
    d = new Date(`${value}T00:00:00`);
  } else {
    d = value instanceof Date ? value : new Date(value);
  }
  return isNaN(d.getTime()) ? null : d;
}

function toDate(value?: string | number | Date | null): Date | null {
  return parseDate(value);
}

/** 'en-IN' for an India outlet (renders dd/mm/yyyy), 'en-US' otherwise
 *  (renders mm/dd/yyyy) — reuses the same IN/US country matching already
 *  used for the currency symbol so the two never disagree. */
export function resolveDateLocale(country?: string | null): 'en-IN' | 'en-US' {
  return getCurrencySymbolForCountry(country) === '₹' ? 'en-IN' : 'en-US';
}

/**
 * Formats a date value as dd/mm/yyyy or mm/dd/yyyy depending on `country`
 * (e.g. 07/15/2026 for US, 15/07/2026 for India). Both always show a 4-digit
 * year. Returns `fallback` (default '') for empty/invalid input.
 */
export function formatDate(value?: string | number | Date | null, country?: string | null, fallback = ''): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(resolveDateLocale(country), { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/**
 * Formats a date value as dd/mm/yyyy, h:mm AM/PM or mm/dd/yyyy, h:mm AM/PM
 * depending on `country`. Returns `fallback` (default '') for empty/invalid input.
 */
export function formatDateTime(value?: string | number | Date | null, country?: string | null, fallback = ''): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleString(resolveDateLocale(country), {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
